// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import React from 'react';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { cleanLocalStorageValues } from '../components/generic/forms/Form';
import { Loading } from '../components/generic/Loading';
import { buildLoginUrl, resolveCallbackUrl } from '../utils/authRedirect';
import { PERMISSION_GROUP_MAP } from './permissions';

export function AuthPageGate({ children, permissions, isPublic }) {
  const router = useRouter();
  const {hasPermission, user, status} = usePermissions(permissions);
  const isLogin = router.pathname.startsWith('/auth/login');
  const {data: session} = useSession();

  React.useEffect(() => {
    if (status === 'loading' || isPublic) return;

    if (isLogin && user) {
      router.push(resolveCallbackUrl(router.query));
      return;
    }

    if (isLogin) {
      cleanLocalStorageValues();
    }

    if (!user && !isLogin) {
      cleanLocalStorageValues();
      router.push(
        buildLoginUrl(router.asPath || '/', { error: 'SessionTimeout' }),
      );
      return;
    }
    if (!hasPermission) {
      router.push('/auth/error');
      return;
    }
  }, [status, hasPermission, user, isPublic, router, isLogin, session]);

  if (isPublic || isLogin) return children;
  if (status != 'authenticated') return <Loading fullScreen />;

  return children;
}

export function TenantSelectionPageGate({children, isPublic}) {
  const router = useRouter();
  const {data: userSession} = useSession();
  if (
    !router.pathname.startsWith('/auth/tenant-select') &&
    !router.pathname.startsWith('/completar-cadastro') && // precisa tirar para não causar loop infinito
    userSession?.user &&
    userSession?.user?.tenant_id == null &&
    !isPublic
  ) {
    router.push(
      `/auth/tenant-select?callbackUrl=` + encodeURIComponent(router.asPath),
    );
    return null;
  }
  return children;
}

export function CompletarPerfilPageGate({children, isPublic}) {
  const router = useRouter();
  const {data: userSession} = useSession();

  if (isPublic || !userSession?.user) {
    return children;
  }

  // Adicionar aqui condições extras para completar o cadastro que sejam relevantes para cada empresa
  if (!userSession?.user?.name) {
    if (router.pathname.startsWith('/completar-cadastro')) {
      return children;
    } 
      router.push(
        `/completar-cadastro?callbackUrl=` +
          encodeURIComponent(router.asPath),
      );
      return null;
    
  } 
    if (!router.pathname.startsWith('/completar-cadastro')) {
      return children;
    } 
      router.push('/');
      return null;
    
  
}


export function AuthGate({ children, permissions }) {
  const { hasPermission, status } = usePermissions(permissions);

  if (status != 'authenticated') return null;

  if (hasPermission) return children;
  return null;
}

export function usePermissions(permissions) {
  const { data: session, status } = useSession();
  if (status == 'loading')
    return {
      hasPermission: false,
      user: null,
      status: status,
    };

  const user = session?.user;

  return {
    hasPermission: userHasPermission(user, permissions),
    user: user,
    status: status,
  };
}

function userHasPermission(user, requiredPermissions) {
  if (!requiredPermissions) return true;

  if (!Array.isArray(requiredPermissions))
    requiredPermissions = [requiredPermissions];
  requiredPermissions = requiredPermissions.filter(
    (permission) => !!permission,
  );
  if (requiredPermissions.length == 0) return true;

  const globalPerms = user?.global_permissions || [];

  return requiredPermissions.some((permission) => {
    if (globalPerms.includes(permission)) return true;
    if (!user?.role) return false;
    return PERMISSION_GROUP_MAP[permission]?.includes(user.role);
  });
}
